/**
 * Comprehensive State Requirements Configuration
 * All 50 States + DC - Home Care Agency Compliance Requirements
 */

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface BackgroundCheckRequirement {
  id: string;
  label: string;
  required: boolean;
  citation?: string;
}

export interface StateRequirements {
  stateCode: string;
  stateName: string;

  // Licensing
  requiresLicense: boolean;
  licensingAgency: string;
  licensingAgencyAbbr: string;
  licenseType: string;
  licensingWebsite?: string;
  licensingPhone?: string;
  licenseCitation?: string;

  // Background Checks
  backgroundChecks: BackgroundCheckRequirement[];
  backgroundCheckCitation: string;
  fingerprintRequired: boolean;
  fingerprintType?: string; // e.g., "Live Scan", "Level 2", "State Police"

  // Training
  initialTrainingHours: number;
  annualTrainingHours: number;
  trainingCitation: string;

  // Supervision
  supervisionFrequencyDays: number; // Days between required supervisory visits
  supervisionCitation: string;

  // Abuse Registry
  abuseRegistryName: string;
  abuseRegistryRequired: boolean;
  abuseRegistryCitation?: string;

  // Additional state-specific requirements
  additionalRequirements?: string[];
}

// ============================================
// STATE REQUIREMENTS DATA - ALL 50 STATES + DC
// ============================================

export const STATE_REQUIREMENTS: Record<string, StateRequirements> = {
  // ==========================================
  // ALABAMA
  // ==========================================
  AL: {
    stateCode: 'AL',
    stateName: 'Alabama',
    requiresLicense: false, // No state license required
    licensingAgency: 'Alabama Department of Public Health',
    licensingAgencyAbbr: 'ADPH',
    licenseType: 'No state license required',
    licensingWebsite: 'https://www.alabamapublichealth.gov',
    backgroundChecks: [
      { id: 'al_state_criminal', label: 'Alabama State Criminal Background Check', required: true },
      { id: 'al_sex_offender', label: 'Alabama Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Ala. Code § 38-13-3',
    fingerprintRequired: false,
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: 'Federal Medicare CoPs',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'Federal Medicare CoPs',
    abuseRegistryName: 'Alabama Department of Human Resources Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Ala. Code § 38-9-11',
  },

  // ==========================================
  // ALASKA
  // ==========================================
  AK: {
    stateCode: 'AK',
    stateName: 'Alaska',
    requiresLicense: true,
    licensingAgency: 'Alaska Department of Health and Social Services',
    licensingAgencyAbbr: 'DHSS',
    licenseType: 'Home and Community-Based Services License',
    licensingWebsite: 'https://dhss.alaska.gov',
    licensingPhone: '(907) 334-2483',
    licenseCitation: '7 AAC 125',
    backgroundChecks: [
      { id: 'ak_state_criminal', label: 'Alaska State Criminal Background Check', required: true },
      { id: 'ak_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ak_background_check_program', label: 'Background Check Program (BCP)', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'AS 47.05.310',
    fingerprintRequired: true,
    fingerprintType: 'State and FBI Fingerprint',
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: '7 AAC 125.090',
    supervisionFrequencyDays: 60,
    supervisionCitation: '7 AAC 125.100',
    abuseRegistryName: 'Alaska Prior Abuse/Neglect Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'AS 47.05.330',
  },

  // ==========================================
  // ARIZONA
  // ==========================================
  AZ: {
    stateCode: 'AZ',
    stateName: 'Arizona',
    requiresLicense: true,
    licensingAgency: 'Arizona Department of Health Services',
    licensingAgencyAbbr: 'ADHS',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://www.azdhs.gov',
    licensingPhone: '(602) 364-2536',
    licenseCitation: 'A.R.S. § 36-401',
    backgroundChecks: [
      { id: 'az_fingerprint', label: 'Arizona DPS Fingerprint Clearance Card', required: true },
      { id: 'az_fbi', label: 'FBI Criminal Background Check', required: true },
      { id: 'az_adult_protective', label: 'Adult Protective Services Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'A.R.S. § 36-411',
    fingerprintRequired: true,
    fingerprintType: 'Fingerprint Clearance Card',
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: 'A.A.C. R9-10-1012',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'A.A.C. R9-10-1013',
    abuseRegistryName: 'Arizona Adult Protective Services Central Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'A.R.S. § 46-459',
  },

  // ==========================================
  // ARKANSAS
  // ==========================================
  AR: {
    stateCode: 'AR',
    stateName: 'Arkansas',
    requiresLicense: true,
    licensingAgency: 'Arkansas Department of Health',
    licensingAgencyAbbr: 'ADH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://www.healthy.arkansas.gov',
    licensingPhone: '(501) 661-2201',
    licenseCitation: 'Ark. Code Ann. § 20-10-1801',
    backgroundChecks: [
      { id: 'ar_state_criminal', label: 'Arkansas State Police Criminal Check', required: true },
      { id: 'ar_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ar_child_maltreatment', label: 'Child Maltreatment Central Registry', required: true },
      { id: 'ar_adult_maltreatment', label: 'Adult Maltreatment Central Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Ark. Code Ann. § 20-38-101',
    fingerprintRequired: true,
    fingerprintType: 'State Police Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'ADH Rules for Home Health Agencies',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'ADH Rules for Home Health Agencies',
    abuseRegistryName: 'Arkansas Adult/Child Maltreatment Central Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Ark. Code Ann. § 12-18-901',
  },

  // ==========================================
  // CALIFORNIA
  // ==========================================
  CA: {
    stateCode: 'CA',
    stateName: 'California',
    requiresLicense: true,
    licensingAgency: 'California Department of Social Services',
    licensingAgencyAbbr: 'CDSS',
    licenseType: 'Home Care Organization (HCO) License',
    licensingWebsite: 'https://www.cdss.ca.gov/inforesources/home-care-services',
    licensingPhone: '(916) 651-8848',
    licenseCitation: 'HSC § 1796.10',
    backgroundChecks: [
      { id: 'ca_doj_live_scan', label: 'California DOJ Live Scan', required: true },
      { id: 'ca_fbi_live_scan', label: 'FBI Live Scan Fingerprint', required: true },
      { id: 'ca_hcsb', label: 'Home Care Services Bureau (HCSB) Registration', required: true },
      { id: 'ca_sex_offender', label: 'Megan\'s Law (Sex Offender) Database', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'HSC § 1736.5',
    fingerprintRequired: true,
    fingerprintType: 'Live Scan',
    initialTrainingHours: 40,
    annualTrainingHours: 5,
    trainingCitation: 'HSC § 1796.63',
    supervisionFrequencyDays: 180,
    supervisionCitation: 'HSC § 1796.60',
    abuseRegistryName: 'Child Abuse Central Index (CACI)',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Penal Code § 11170',
    additionalRequirements: [
      'Home Care Aide (HCA) Registration required for all aides',
      'Aides must be registered with HCSB before providing services',
    ],
  },

  // ==========================================
  // COLORADO
  // ==========================================
  CO: {
    stateCode: 'CO',
    stateName: 'Colorado',
    requiresLicense: true,
    licensingAgency: 'Colorado Department of Public Health and Environment',
    licensingAgencyAbbr: 'CDPHE',
    licenseType: 'Home Care Agency License',
    licensingWebsite: 'https://cdphe.colorado.gov',
    licensingPhone: '(303) 692-2800',
    licenseCitation: '6 CCR 1011-1 Chapter 26',
    backgroundChecks: [
      { id: 'co_cbi', label: 'Colorado Bureau of Investigation (CBI) Check', required: true },
      { id: 'co_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'co_caps', label: 'Colorado Adult Protective Services (CAPS)', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'C.R.S. § 27-90-111',
    fingerprintRequired: true,
    fingerprintType: 'CBI and FBI Fingerprint',
    initialTrainingHours: 60,
    annualTrainingHours: 12,
    trainingCitation: '6 CCR 1011-1 Chapter 26',
    supervisionFrequencyDays: 60,
    supervisionCitation: '6 CCR 1011-1 Chapter 26',
    abuseRegistryName: 'Colorado Adult Protective Services Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'C.R.S. § 26-3.1-111',
  },

  // ==========================================
  // CONNECTICUT
  // ==========================================
  CT: {
    stateCode: 'CT',
    stateName: 'Connecticut',
    requiresLicense: true,
    licensingAgency: 'Connecticut Department of Public Health',
    licensingAgencyAbbr: 'DPH',
    licenseType: 'Home Health Care Agency License',
    licensingWebsite: 'https://portal.ct.gov/DPH',
    licensingPhone: '(860) 509-7400',
    licenseCitation: 'Conn. Gen. Stat. § 19a-490',
    backgroundChecks: [
      { id: 'ct_state_criminal', label: 'Connecticut State Police Criminal Check', required: true },
      { id: 'ct_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ct_abuse_neglect', label: 'DCF Abuse/Neglect Registry', required: true },
      { id: 'ct_nurse_aide', label: 'Nurse Aide Registry Check', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Conn. Gen. Stat. § 19a-491c',
    fingerprintRequired: true,
    fingerprintType: 'State and FBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'Conn. Agencies Regs. § 19-13-D66',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'Conn. Agencies Regs. § 19-13-D66',
    abuseRegistryName: 'Connecticut DCF Abuse/Neglect Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Conn. Gen. Stat. § 17a-101k',
  },

  // ==========================================
  // DELAWARE
  // ==========================================
  DE: {
    stateCode: 'DE',
    stateName: 'Delaware',
    requiresLicense: true,
    licensingAgency: 'Delaware Division of Health Care Quality',
    licensingAgencyAbbr: 'DHCQ',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://dhss.delaware.gov/dhss/dhcq',
    licensingPhone: '(302) 292-3930',
    licenseCitation: '16 Del. C. § 1101',
    backgroundChecks: [
      { id: 'de_state_criminal', label: 'Delaware State Bureau of Investigation Check', required: true },
      { id: 'de_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'de_adult_abuse', label: 'Adult Abuse Registry', required: true },
      { id: 'de_child_abuse', label: 'Child Abuse Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: '16 Del. C. § 1141',
    fingerprintRequired: true,
    fingerprintType: 'SBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '16 Del. Admin. Code § 4406',
    supervisionFrequencyDays: 60,
    supervisionCitation: '16 Del. Admin. Code § 4406',
    abuseRegistryName: 'Delaware Adult/Child Abuse Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: '31 Del. C. § 3913',
  },

  // ==========================================
  // DISTRICT OF COLUMBIA
  // ==========================================
  DC: {
    stateCode: 'DC',
    stateName: 'District of Columbia',
    requiresLicense: true,
    licensingAgency: 'DC Department of Health',
    licensingAgencyAbbr: 'DC Health',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://dchealth.dc.gov',
    licensingPhone: '(202) 442-5955',
    licenseCitation: 'D.C. Code § 44-501',
    backgroundChecks: [
      { id: 'dc_mpd', label: 'DC Metropolitan Police Department Check', required: true },
      { id: 'dc_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'dc_nurse_aide', label: 'Nurse Aide Registry Check', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'D.C. Code § 44-551',
    fingerprintRequired: true,
    fingerprintType: 'FBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '22-B DCMR § 9399',
    supervisionFrequencyDays: 60,
    supervisionCitation: '22-B DCMR § 9399',
    abuseRegistryName: 'DC Child Protection Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'D.C. Code § 4-1302.01',
  },

  // ==========================================
  // FLORIDA
  // ==========================================
  FL: {
    stateCode: 'FL',
    stateName: 'Florida',
    requiresLicense: true,
    licensingAgency: 'Florida Agency for Health Care Administration',
    licensingAgencyAbbr: 'AHCA',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://ahca.myflorida.com',
    licensingPhone: '(888) 419-3456',
    licenseCitation: 'F.S. § 400.462',
    backgroundChecks: [
      { id: 'fl_level2', label: 'Level 2 Background Screening (State + FBI Fingerprint)', required: true },
      { id: 'fl_fdle', label: 'FDLE (Florida Department of Law Enforcement) Check', required: true },
      { id: 'fl_abuse_hotline', label: 'Florida Abuse Hotline Registry', required: true },
      { id: 'fl_medicaid_exclusion', label: 'Florida Medicaid Exclusion List', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'F.S. § 408.809',
    fingerprintRequired: true,
    fingerprintType: 'Level 2 Screening',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'F.S. § 400.476',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'F.A.C. 59A-8.0095',
    abuseRegistryName: 'Florida Abuse Hotline Records',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'F.S. § 39.201',
    additionalRequirements: [
      'Level 2 screening must be completed through AHCA Clearinghouse',
      'Rescreening required every 5 years',
    ],
  },

  // ==========================================
  // GEORGIA
  // ==========================================
  GA: {
    stateCode: 'GA',
    stateName: 'Georgia',
    requiresLicense: true,
    licensingAgency: 'Georgia Department of Community Health',
    licensingAgencyAbbr: 'DCH',
    licenseType: 'Private Home Care Provider License',
    licensingWebsite: 'https://dch.georgia.gov',
    licensingPhone: '(404) 657-5700',
    licenseCitation: 'O.C.G.A. § 31-7-300',
    backgroundChecks: [
      { id: 'ga_gcic', label: 'Georgia Crime Information Center (GCIC) Check', required: true },
      { id: 'ga_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ga_sex_offender', label: 'Georgia Sex Offender Registry', required: true },
      { id: 'ga_nurse_aide', label: 'Nurse Aide Registry Check', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'O.C.G.A. § 31-7-353',
    fingerprintRequired: true,
    fingerprintType: 'GCIC Fingerprint',
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: 'Ga. Comp. R. & Regs. r. 111-8-65',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'Ga. Comp. R. & Regs. r. 111-8-65',
    abuseRegistryName: 'Georgia Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'O.C.G.A. § 31-7-351',
  },

  // ==========================================
  // HAWAII
  // ==========================================
  HI: {
    stateCode: 'HI',
    stateName: 'Hawaii',
    requiresLicense: true,
    licensingAgency: 'Hawaii State Department of Health',
    licensingAgencyAbbr: 'DOH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://health.hawaii.gov',
    licensingPhone: '(808) 692-7420',
    licenseCitation: 'HRS § 321-11',
    backgroundChecks: [
      { id: 'hi_hcjdc', label: 'Hawaii Criminal Justice Data Center Check', required: true },
      { id: 'hi_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'hi_adult_abuse', label: 'Adult Protective Services Registry', required: true },
      { id: 'hi_nurse_aide', label: 'Nurse Aide Registry Check', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'HRS § 846-2.7',
    fingerprintRequired: true,
    fingerprintType: 'State and FBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'HAR § 11-97',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'HAR § 11-97',
    abuseRegistryName: 'Hawaii Adult Protective Services Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'HRS § 346-224',
  },

  // ==========================================
  // IDAHO
  // ==========================================
  ID: {
    stateCode: 'ID',
    stateName: 'Idaho',
    requiresLicense: false, // No state license required for non-medical
    licensingAgency: 'Idaho Department of Health and Welfare',
    licensingAgencyAbbr: 'DHW',
    licenseType: 'No state license required for non-medical home care',
    licensingWebsite: 'https://healthandwelfare.idaho.gov',
    backgroundChecks: [
      { id: 'id_isp', label: 'Idaho State Police Criminal Check', required: true },
      { id: 'id_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'id_adult_abuse', label: 'Adult Protection Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Idaho Code § 39-5601',
    fingerprintRequired: true,
    fingerprintType: 'ISP Fingerprint',
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: 'IDAPA 16.03.10',
    supervisionFrequencyDays: 90,
    supervisionCitation: 'IDAPA 16.03.10',
    abuseRegistryName: 'Idaho Adult Protection Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Idaho Code § 39-5310',
  },

  // ==========================================
  // ILLINOIS
  // ==========================================
  IL: {
    stateCode: 'IL',
    stateName: 'Illinois',
    requiresLicense: true,
    licensingAgency: 'Illinois Department of Public Health',
    licensingAgencyAbbr: 'IDPH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://dph.illinois.gov',
    licensingPhone: '(217) 782-2913',
    licenseCitation: '210 ILCS 55/',
    backgroundChecks: [
      { id: 'il_isp', label: 'Illinois State Police Criminal Check', required: true },
      { id: 'il_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'il_hfs', label: 'Healthcare Worker Background Check (HFS)', required: true },
      { id: 'il_sex_offender', label: 'Illinois Sex Offender Registry', required: true },
      { id: 'il_nurse_aide', label: 'Illinois Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: '225 ILCS 46/',
    fingerprintRequired: true,
    fingerprintType: 'ISP Fingerprint',
    initialTrainingHours: 120,
    annualTrainingHours: 12,
    trainingCitation: '77 Ill. Adm. Code 245.40',
    supervisionFrequencyDays: 60,
    supervisionCitation: '77 Ill. Adm. Code 245.50',
    abuseRegistryName: 'Illinois Health Care Worker Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: '225 ILCS 46/25',
  },

  // ==========================================
  // INDIANA
  // ==========================================
  IN: {
    stateCode: 'IN',
    stateName: 'Indiana',
    requiresLicense: true,
    licensingAgency: 'Indiana State Department of Health',
    licensingAgencyAbbr: 'ISDH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://www.in.gov/health',
    licensingPhone: '(317) 233-7474',
    licenseCitation: 'IC 16-27-1',
    backgroundChecks: [
      { id: 'in_limited', label: 'Indiana Limited Criminal History Check', required: true },
      { id: 'in_expanded', label: 'Indiana Expanded Criminal History Check', required: true },
      { id: 'in_nurse_aide', label: 'Indiana Nurse Aide Registry', required: true },
      { id: 'in_sex_offender', label: 'Indiana Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'IC 16-27-2',
    fingerprintRequired: false,
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '410 IAC 17-11',
    supervisionFrequencyDays: 60,
    supervisionCitation: '410 IAC 17-11',
    abuseRegistryName: 'Indiana Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'IC 16-28-13',
  },

  // ==========================================
  // IOWA
  // ==========================================
  IA: {
    stateCode: 'IA',
    stateName: 'Iowa',
    requiresLicense: true,
    licensingAgency: 'Iowa Department of Inspections and Appeals',
    licensingAgencyAbbr: 'DIA',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://dia.iowa.gov',
    licensingPhone: '(515) 281-4115',
    licenseCitation: 'Iowa Code § 135C',
    backgroundChecks: [
      { id: 'ia_dci', label: 'Iowa Division of Criminal Investigation Check', required: true },
      { id: 'ia_dependent_abuse', label: 'Dependent Adult Abuse Registry', required: true },
      { id: 'ia_child_abuse', label: 'Child Abuse Registry', required: true },
      { id: 'ia_sex_offender', label: 'Iowa Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Iowa Code § 135C.33',
    fingerprintRequired: false,
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'IAC 481-57',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'IAC 481-57',
    abuseRegistryName: 'Iowa Dependent Adult/Child Abuse Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Iowa Code § 235B.6',
  },

  // ==========================================
  // KANSAS
  // ==========================================
  KS: {
    stateCode: 'KS',
    stateName: 'Kansas',
    requiresLicense: true,
    licensingAgency: 'Kansas Department for Aging and Disability Services',
    licensingAgencyAbbr: 'KDADS',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://kdads.ks.gov',
    licensingPhone: '(785) 296-4986',
    licenseCitation: 'K.S.A. 65-5101',
    backgroundChecks: [
      { id: 'ks_kbi', label: 'Kansas Bureau of Investigation (KBI) Check', required: true },
      { id: 'ks_adult_abuse', label: 'Kansas Adult Abuse Registry', required: true },
      { id: 'ks_child_abuse', label: 'Kansas Child Abuse Registry', required: true },
      { id: 'ks_nurse_aide', label: 'Kansas Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'K.S.A. 39-970',
    fingerprintRequired: false,
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: 'K.A.R. 26-41',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'K.A.R. 26-41',
    abuseRegistryName: 'Kansas Adult/Child Abuse Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'K.S.A. 39-1431',
  },

  // ==========================================
  // KENTUCKY
  // ==========================================
  KY: {
    stateCode: 'KY',
    stateName: 'Kentucky',
    requiresLicense: true,
    licensingAgency: 'Kentucky Cabinet for Health and Family Services',
    licensingAgencyAbbr: 'CHFS',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://chfs.ky.gov',
    licensingPhone: '(502) 564-2800',
    licenseCitation: 'KRS 216.935',
    backgroundChecks: [
      { id: 'ky_ksp', label: 'Kentucky State Police Criminal Check', required: true },
      { id: 'ky_cabinet_check', label: 'Cabinet for Health and Family Services Check', required: true },
      { id: 'ky_nurse_aide', label: 'Kentucky Nurse Aide Registry', required: true },
      { id: 'ky_sex_offender', label: 'Kentucky Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'KRS 216.789',
    fingerprintRequired: true,
    fingerprintType: 'KSP Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '902 KAR 20:081',
    supervisionFrequencyDays: 60,
    supervisionCitation: '902 KAR 20:081',
    abuseRegistryName: 'Kentucky Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'KRS 216.532',
  },

  // ==========================================
  // LOUISIANA
  // ==========================================
  LA: {
    stateCode: 'LA',
    stateName: 'Louisiana',
    requiresLicense: true,
    licensingAgency: 'Louisiana Department of Health',
    licensingAgencyAbbr: 'LDH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://ldh.la.gov',
    licensingPhone: '(225) 342-0138',
    licenseCitation: 'La. R.S. 40:2116',
    backgroundChecks: [
      { id: 'la_state_police', label: 'Louisiana State Police Criminal Check', required: true },
      { id: 'la_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'la_dese', label: 'Direct Service Worker Registry', required: true },
      { id: 'la_sex_offender', label: 'Louisiana Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'La. R.S. 40:1203.3',
    fingerprintRequired: true,
    fingerprintType: 'State Police Fingerprint',
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: 'LAC 48:I.Chapter 89',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'LAC 48:I.Chapter 89',
    abuseRegistryName: 'Louisiana Direct Service Worker Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'La. R.S. 40:1203.2',
  },

  // ==========================================
  // MAINE
  // ==========================================
  ME: {
    stateCode: 'ME',
    stateName: 'Maine',
    requiresLicense: true,
    licensingAgency: 'Maine Department of Health and Human Services',
    licensingAgencyAbbr: 'DHHS',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://www.maine.gov/dhhs',
    licensingPhone: '(207) 287-9300',
    licenseCitation: '22 M.R.S.A. § 2141',
    backgroundChecks: [
      { id: 'me_sbi', label: 'Maine State Bureau of Identification Check', required: true },
      { id: 'me_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'me_adult_abuse', label: 'Maine Adult Protective Services Registry', required: true },
      { id: 'me_nurse_aide', label: 'Maine Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: '22 M.R.S.A. § 1812-G',
    fingerprintRequired: true,
    fingerprintType: 'SBI Fingerprint',
    initialTrainingHours: 50,
    annualTrainingHours: 12,
    trainingCitation: '10-144 CMR Chapter 12',
    supervisionFrequencyDays: 60,
    supervisionCitation: '10-144 CMR Chapter 12',
    abuseRegistryName: 'Maine Adult Protective Services Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: '22 M.R.S.A. § 3477',
  },

  // ==========================================
  // MARYLAND
  // ==========================================
  MD: {
    stateCode: 'MD',
    stateName: 'Maryland',
    requiresLicense: true,
    licensingAgency: 'Maryland Department of Health',
    licensingAgencyAbbr: 'MDH',
    licenseType: 'Residential Service Agency (RSA) License',
    licensingWebsite: 'https://health.maryland.gov',
    licensingPhone: '(410) 402-8000',
    licenseCitation: 'COMAR 10.07.05',
    backgroundChecks: [
      { id: 'md_cjis', label: 'Maryland CJIS Criminal Check', required: true },
      { id: 'md_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'md_nurse_aide', label: 'Maryland Nurse Aide Registry', required: true },
      { id: 'md_sex_offender', label: 'Maryland Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Health-General § 19-1901',
    fingerprintRequired: true,
    fingerprintType: 'CJIS Fingerprint',
    initialTrainingHours: 80,
    annualTrainingHours: 12,
    trainingCitation: 'COMAR 10.07.05.10',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'COMAR 10.07.05.12',
    abuseRegistryName: 'Maryland Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'COMAR 10.07.02',
  },

  // ==========================================
  // MASSACHUSETTS
  // ==========================================
  MA: {
    stateCode: 'MA',
    stateName: 'Massachusetts',
    requiresLicense: true,
    licensingAgency: 'Massachusetts Department of Public Health',
    licensingAgencyAbbr: 'DPH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://www.mass.gov/orgs/department-of-public-health',
    licensingPhone: '(617) 624-6000',
    licenseCitation: '105 CMR 141.000',
    backgroundChecks: [
      { id: 'ma_cori', label: 'CORI (Criminal Offender Record Information) Check', required: true },
      { id: 'ma_sori', label: 'SORI (Sex Offender Registry Information) Check', required: true },
      { id: 'ma_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ma_nurse_aide', label: 'Massachusetts Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'M.G.L. c. 111 § 72B',
    fingerprintRequired: true,
    fingerprintType: 'State and FBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '105 CMR 141.050',
    supervisionFrequencyDays: 60,
    supervisionCitation: '105 CMR 141.060',
    abuseRegistryName: 'Massachusetts Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: '105 CMR 157.000',
  },

  // ==========================================
  // MICHIGAN
  // ==========================================
  MI: {
    stateCode: 'MI',
    stateName: 'Michigan',
    requiresLicense: false, // No state license required for non-medical
    licensingAgency: 'Michigan Department of Licensing and Regulatory Affairs',
    licensingAgencyAbbr: 'LARA',
    licenseType: 'No state license required for non-medical home care',
    licensingWebsite: 'https://www.michigan.gov/lara',
    backgroundChecks: [
      { id: 'mi_ichat', label: 'Michigan ICHAT Criminal Check', required: true },
      { id: 'mi_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'mi_offender_tracking', label: 'Michigan Offender Tracking Information System', required: true },
      { id: 'mi_nurse_aide', label: 'Michigan Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'MCL 333.20173a',
    fingerprintRequired: true,
    fingerprintType: 'State and FBI Fingerprint',
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: 'Federal Medicare CoPs',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'Federal Medicare CoPs',
    abuseRegistryName: 'Michigan Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'MCL 333.21776',
  },

  // ==========================================
  // MINNESOTA
  // ==========================================
  MN: {
    stateCode: 'MN',
    stateName: 'Minnesota',
    requiresLicense: true,
    licensingAgency: 'Minnesota Department of Health',
    licensingAgencyAbbr: 'MDH',
    licenseType: 'Home Care Provider License',
    licensingWebsite: 'https://www.health.state.mn.us',
    licensingPhone: '(651) 201-4101',
    licenseCitation: 'Minn. Stat. § 144A.43',
    backgroundChecks: [
      { id: 'mn_bca', label: 'Minnesota BCA Criminal Check', required: true },
      { id: 'mn_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'mn_netstudy', label: 'NetStudy (Maltreatment Background Study)', required: true },
      { id: 'mn_nurse_aide', label: 'Minnesota Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Minn. Stat. § 245C',
    fingerprintRequired: true,
    fingerprintType: 'BCA and FBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 8,
    trainingCitation: 'Minn. R. 4668.0100',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'Minn. R. 4668.0100',
    abuseRegistryName: 'Minnesota NetStudy Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Minn. Stat. § 245C.04',
  },

  // ==========================================
  // MISSISSIPPI
  // ==========================================
  MS: {
    stateCode: 'MS',
    stateName: 'Mississippi',
    requiresLicense: true,
    licensingAgency: 'Mississippi State Department of Health',
    licensingAgencyAbbr: 'MSDH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://msdh.ms.gov',
    licensingPhone: '(601) 576-7400',
    licenseCitation: 'Miss. Code Ann. § 41-71-1',
    backgroundChecks: [
      { id: 'ms_state', label: 'Mississippi Criminal Information Center Check', required: true },
      { id: 'ms_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ms_nurse_aide', label: 'Mississippi Nurse Aide Registry', required: true },
      { id: 'ms_sex_offender', label: 'Mississippi Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Miss. Code Ann. § 43-11-13',
    fingerprintRequired: true,
    fingerprintType: 'State and FBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '15-16-2 Miss. Admin. Code',
    supervisionFrequencyDays: 60,
    supervisionCitation: '15-16-2 Miss. Admin. Code',
    abuseRegistryName: 'Mississippi Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Miss. Code Ann. § 43-11-13',
  },

  // ==========================================
  // MISSOURI
  // ==========================================
  MO: {
    stateCode: 'MO',
    stateName: 'Missouri',
    requiresLicense: true,
    licensingAgency: 'Missouri Department of Health and Senior Services',
    licensingAgencyAbbr: 'DHSS',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://health.mo.gov',
    licensingPhone: '(573) 751-6303',
    licenseCitation: 'Mo. Rev. Stat. § 197.400',
    backgroundChecks: [
      { id: 'mo_mshp', label: 'Missouri State Highway Patrol Criminal Check', required: true },
      { id: 'mo_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'mo_employee_disqualification', label: 'Employee Disqualification List', required: true },
      { id: 'mo_sex_offender', label: 'Missouri Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Mo. Rev. Stat. § 660.317',
    fingerprintRequired: true,
    fingerprintType: 'MSHP Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '19 CSR 30-82',
    supervisionFrequencyDays: 60,
    supervisionCitation: '19 CSR 30-82',
    abuseRegistryName: 'Missouri Employee Disqualification List',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Mo. Rev. Stat. § 660.315',
  },

  // ==========================================
  // MONTANA
  // ==========================================
  MT: {
    stateCode: 'MT',
    stateName: 'Montana',
    requiresLicense: true,
    licensingAgency: 'Montana Department of Public Health and Human Services',
    licensingAgencyAbbr: 'DPHHS',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://dphhs.mt.gov',
    licensingPhone: '(406) 444-2037',
    licenseCitation: 'Mont. Code Ann. § 50-5-101',
    backgroundChecks: [
      { id: 'mt_doj', label: 'Montana Department of Justice Criminal Check', required: true },
      { id: 'mt_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'mt_nurse_aide', label: 'Montana Nurse Aide Registry', required: true },
      { id: 'mt_sex_offender', label: 'Montana Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Mont. Code Ann. § 50-5-410',
    fingerprintRequired: true,
    fingerprintType: 'DOJ Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'ARM 37.106.1820',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'ARM 37.106.1820',
    abuseRegistryName: 'Montana Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Mont. Code Ann. § 50-5-201',
  },

  // ==========================================
  // NEBRASKA
  // ==========================================
  NE: {
    stateCode: 'NE',
    stateName: 'Nebraska',
    requiresLicense: true,
    licensingAgency: 'Nebraska Department of Health and Human Services',
    licensingAgencyAbbr: 'DHHS',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://dhhs.ne.gov',
    licensingPhone: '(402) 471-2133',
    licenseCitation: 'Neb. Rev. Stat. § 71-401',
    backgroundChecks: [
      { id: 'ne_nsp', label: 'Nebraska State Patrol Criminal Check', required: true },
      { id: 'ne_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ne_adult_abuse', label: 'Nebraska Adult Protective Services Registry', required: true },
      { id: 'ne_nurse_aide', label: 'Nebraska Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Neb. Rev. Stat. § 71-6318',
    fingerprintRequired: true,
    fingerprintType: 'NSP Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '175 NAC 9',
    supervisionFrequencyDays: 60,
    supervisionCitation: '175 NAC 9',
    abuseRegistryName: 'Nebraska Adult Protective Services Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Neb. Rev. Stat. § 28-380',
  },

  // ==========================================
  // NEVADA
  // ==========================================
  NV: {
    stateCode: 'NV',
    stateName: 'Nevada',
    requiresLicense: true,
    licensingAgency: 'Nevada Division of Public and Behavioral Health',
    licensingAgencyAbbr: 'DPBH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://dpbh.nv.gov',
    licensingPhone: '(775) 684-1030',
    licenseCitation: 'NRS 449.030',
    backgroundChecks: [
      { id: 'nv_dps', label: 'Nevada Department of Public Safety Criminal Check', required: true },
      { id: 'nv_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'nv_nurse_aide', label: 'Nevada State Board of Nursing Aide Registry', required: true },
      { id: 'nv_sex_offender', label: 'Nevada Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'NRS 449.188',
    fingerprintRequired: true,
    fingerprintType: 'DPS Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'NAC 449.730',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'NAC 449.730',
    abuseRegistryName: 'Nevada Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'NRS 632.344',
  },

  // ==========================================
  // NEW HAMPSHIRE
  // ==========================================
  NH: {
    stateCode: 'NH',
    stateName: 'New Hampshire',
    requiresLicense: true,
    licensingAgency: 'New Hampshire Department of Health and Human Services',
    licensingAgencyAbbr: 'DHHS',
    licenseType: 'Home Health Care Provider License',
    licensingWebsite: 'https://www.dhhs.nh.gov',
    licensingPhone: '(603) 271-9499',
    licenseCitation: 'RSA 151:2',
    backgroundChecks: [
      { id: 'nh_state', label: 'New Hampshire State Police Criminal Check', required: true },
      { id: 'nh_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'nh_abuse_registry', label: 'State Registry of Founded Reports', required: true },
      { id: 'nh_nurse_aide', label: 'New Hampshire Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'RSA 151:2-d',
    fingerprintRequired: true,
    fingerprintType: 'State and FBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'He-P 809',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'He-P 809',
    abuseRegistryName: 'NH Registry of Founded Reports',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'RSA 169-C:35',
  },

  // ==========================================
  // NEW JERSEY
  // ==========================================
  NJ: {
    stateCode: 'NJ',
    stateName: 'New Jersey',
    requiresLicense: true,
    licensingAgency: 'New Jersey Department of Health',
    licensingAgencyAbbr: 'DOH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://www.nj.gov/health',
    licensingPhone: '(609) 292-7837',
    licenseCitation: 'N.J.S.A. 26:2H-1',
    backgroundChecks: [
      { id: 'nj_state', label: 'New Jersey State Police Criminal Check', required: true },
      { id: 'nj_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'nj_chri', label: 'Criminal History Record Information (CHRI)', required: true },
      { id: 'nj_nurse_aide', label: 'New Jersey Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'N.J.S.A. 26:2H-83',
    fingerprintRequired: true,
    fingerprintType: 'State and FBI Fingerprint',
    initialTrainingHours: 76,
    annualTrainingHours: 12,
    trainingCitation: 'N.J.A.C. 8:42',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'N.J.A.C. 8:42',
    abuseRegistryName: 'NJ Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'N.J.A.C. 8:85',
  },

  // ==========================================
  // NEW MEXICO
  // ==========================================
  NM: {
    stateCode: 'NM',
    stateName: 'New Mexico',
    requiresLicense: true,
    licensingAgency: 'New Mexico Department of Health',
    licensingAgencyAbbr: 'NMDOH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://nmhealth.org',
    licensingPhone: '(505) 476-9025',
    licenseCitation: 'NMSA 1978 § 24-1-5',
    backgroundChecks: [
      { id: 'nm_dps', label: 'New Mexico Department of Public Safety Criminal Check', required: true },
      { id: 'nm_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'nm_employee_abuse', label: 'Employee Abuse Registry', required: true },
      { id: 'nm_nurse_aide', label: 'New Mexico Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'NMSA 1978 § 29-17-5',
    fingerprintRequired: true,
    fingerprintType: 'DPS Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '7.28.2 NMAC',
    supervisionFrequencyDays: 60,
    supervisionCitation: '7.28.2 NMAC',
    abuseRegistryName: 'New Mexico Employee Abuse Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'NMSA 1978 § 27-7a-1',
  },

  // ==========================================
  // NEW YORK
  // ==========================================
  NY: {
    stateCode: 'NY',
    stateName: 'New York',
    requiresLicense: true,
    licensingAgency: 'New York State Department of Health',
    licensingAgencyAbbr: 'NYSDOH',
    licenseType: 'Licensed Home Care Services Agency (LHCSA)',
    licensingWebsite: 'https://www.health.ny.gov',
    licensingPhone: '(518) 408-1133',
    licenseCitation: 'PHL Article 36',
    backgroundChecks: [
      { id: 'ny_dcjs', label: 'DCJS Criminal History Record Check', required: true },
      { id: 'ny_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ny_staff_exclusion', label: 'Staff Exclusion List (SEL)', required: true },
      { id: 'ny_nurse_aide', label: 'New York Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'PHL § 3606',
    fingerprintRequired: true,
    fingerprintType: 'DCJS Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '10 NYCRR 766.11',
    supervisionFrequencyDays: 60,
    supervisionCitation: '10 NYCRR 763.13',
    abuseRegistryName: 'New York Staff Exclusion List (SEL)',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'SSL § 495',
    additionalRequirements: [
      'LHCSA license must be obtained before operating',
      'Home health aides must complete 75-hour training program',
    ],
  },

  // ==========================================
  // NORTH CAROLINA
  // ==========================================
  NC: {
    stateCode: 'NC',
    stateName: 'North Carolina',
    requiresLicense: true,
    licensingAgency: 'North Carolina Division of Health Service Regulation',
    licensingAgencyAbbr: 'DHSR',
    licenseType: 'Home Care Agency License',
    licensingWebsite: 'https://info.ncdhhs.gov/dhsr',
    licensingPhone: '(919) 855-3765',
    licenseCitation: 'G.S. 131E-136',
    backgroundChecks: [
      { id: 'nc_state', label: 'North Carolina State Bureau of Investigation Check', required: true },
      { id: 'nc_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'nc_hcpr', label: 'Health Care Personnel Registry', required: true },
      { id: 'nc_sex_offender', label: 'NC Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'G.S. 131E-265',
    fingerprintRequired: true,
    fingerprintType: 'SBI Fingerprint',
    initialTrainingHours: 80,
    annualTrainingHours: 12,
    trainingCitation: '10A NCAC 13J .1103',
    supervisionFrequencyDays: 60,
    supervisionCitation: '10A NCAC 13J .1105',
    abuseRegistryName: 'NC Health Care Personnel Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'G.S. 131E-256',
  },

  // ==========================================
  // NORTH DAKOTA
  // ==========================================
  ND: {
    stateCode: 'ND',
    stateName: 'North Dakota',
    requiresLicense: true,
    licensingAgency: 'North Dakota Department of Health',
    licensingAgencyAbbr: 'NDDOH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://www.health.nd.gov',
    licensingPhone: '(701) 328-2352',
    licenseCitation: 'N.D.C.C. § 23-17.3',
    backgroundChecks: [
      { id: 'nd_bci', label: 'ND Bureau of Criminal Investigation Check', required: true },
      { id: 'nd_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'nd_nurse_aide', label: 'North Dakota Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'N.D.C.C. § 12-60-24',
    fingerprintRequired: true,
    fingerprintType: 'BCI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'N.D.A.C. § 33-03-24.1',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'N.D.A.C. § 33-03-24.1',
    abuseRegistryName: 'ND Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'N.D.C.C. § 43-12.1-09.1',
  },

  // ==========================================
  // OHIO
  // ==========================================
  OH: {
    stateCode: 'OH',
    stateName: 'Ohio',
    requiresLicense: true,
    licensingAgency: 'Ohio Department of Health',
    licensingAgencyAbbr: 'ODH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://odh.ohio.gov',
    licensingPhone: '(614) 466-3543',
    licenseCitation: 'ORC § 3701.881',
    backgroundChecks: [
      { id: 'oh_bcii', label: 'Ohio BCI&I Criminal Check', required: true },
      { id: 'oh_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'oh_nurse_aide', label: 'Ohio Nurse Aide Registry', required: true },
      { id: 'oh_rapback', label: 'Ohio Rap Back Program', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'ORC § 3701.881',
    fingerprintRequired: true,
    fingerprintType: 'BCI&I Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'OAC 3701-17',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'OAC 3701-17',
    abuseRegistryName: 'Ohio Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'ORC § 3721.32',
  },

  // ==========================================
  // OKLAHOMA
  // ==========================================
  OK: {
    stateCode: 'OK',
    stateName: 'Oklahoma',
    requiresLicense: true,
    licensingAgency: 'Oklahoma State Department of Health',
    licensingAgencyAbbr: 'OSDH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://oklahoma.gov/health',
    licensingPhone: '(405) 271-6576',
    licenseCitation: '63 O.S. § 1-1903',
    backgroundChecks: [
      { id: 'ok_osbi', label: 'OSBI Criminal History Check', required: true },
      { id: 'ok_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ok_community_services', label: 'Community Services Worker Registry', required: true },
      { id: 'ok_sex_offender', label: 'Oklahoma Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: '63 O.S. § 1-1950.1',
    fingerprintRequired: true,
    fingerprintType: 'OSBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'OAC 310:605',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'OAC 310:605',
    abuseRegistryName: 'Oklahoma Community Services Worker Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: '56 O.S. § 1025.2',
  },

  // ==========================================
  // OREGON
  // ==========================================
  OR: {
    stateCode: 'OR',
    stateName: 'Oregon',
    requiresLicense: true,
    licensingAgency: 'Oregon Health Authority',
    licensingAgencyAbbr: 'OHA',
    licenseType: 'In-Home Care Agency License',
    licensingWebsite: 'https://www.oregon.gov/oha',
    licensingPhone: '(503) 945-5944',
    licenseCitation: 'ORS 443.315',
    backgroundChecks: [
      { id: 'or_osp', label: 'Oregon State Police Criminal Check', required: true },
      { id: 'or_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'or_abuse_registry', label: 'Oregon Abuse Investigation Registry', required: true },
      { id: 'or_nurse_aide', label: 'Oregon Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'ORS 443.004',
    fingerprintRequired: true,
    fingerprintType: 'OSP Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'OAR 333-027',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'OAR 333-027',
    abuseRegistryName: 'Oregon Abuse Investigation Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'ORS 443.006',
  },

  // ==========================================
  // PENNSYLVANIA
  // ==========================================
  PA: {
    stateCode: 'PA',
    stateName: 'Pennsylvania',
    requiresLicense: true,
    licensingAgency: 'Pennsylvania Department of Health',
    licensingAgencyAbbr: 'DOH',
    licenseType: 'Home Health Care Agency License',
    licensingWebsite: 'https://www.health.pa.gov',
    licensingPhone: '(717) 783-1379',
    licenseCitation: '28 Pa. Code § 601.1',
    backgroundChecks: [
      { id: 'pa_psp', label: 'Pennsylvania State Police Criminal Check', required: true },
      { id: 'pa_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'pa_childline', label: 'ChildLine (Child Abuse Registry)', required: true },
      { id: 'pa_nurse_aide', label: 'Pennsylvania Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: '35 P.S. § 10225.501',
    fingerprintRequired: true,
    fingerprintType: 'PSP Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '28 Pa. Code § 611.55',
    supervisionFrequencyDays: 60,
    supervisionCitation: '28 Pa. Code § 611.57',
    abuseRegistryName: 'Pennsylvania ChildLine and Abuse Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: '23 Pa.C.S. § 6336',
  },

  // ==========================================
  // RHODE ISLAND
  // ==========================================
  RI: {
    stateCode: 'RI',
    stateName: 'Rhode Island',
    requiresLicense: true,
    licensingAgency: 'Rhode Island Department of Health',
    licensingAgencyAbbr: 'RIDOH',
    licenseType: 'Home Care Agency License',
    licensingWebsite: 'https://health.ri.gov',
    licensingPhone: '(401) 222-5960',
    licenseCitation: 'R.I. Gen. Laws § 23-17-38',
    backgroundChecks: [
      { id: 'ri_bci', label: 'Rhode Island BCI Criminal Check', required: true },
      { id: 'ri_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ri_dcyf', label: 'DCYF (Child Abuse Registry)', required: true },
      { id: 'ri_nurse_aide', label: 'Rhode Island Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'R.I. Gen. Laws § 23-17-37',
    fingerprintRequired: true,
    fingerprintType: 'BCI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '216-RICR-40-10-3',
    supervisionFrequencyDays: 60,
    supervisionCitation: '216-RICR-40-10-3',
    abuseRegistryName: 'RI DCYF Central Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'R.I. Gen. Laws § 40-11-7.1',
  },

  // ==========================================
  // SOUTH CAROLINA
  // ==========================================
  SC: {
    stateCode: 'SC',
    stateName: 'South Carolina',
    requiresLicense: true,
    licensingAgency: 'South Carolina Department of Health and Environmental Control',
    licensingAgencyAbbr: 'DHEC',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://scdhec.gov',
    licensingPhone: '(803) 545-4370',
    licenseCitation: 'S.C. Code Ann. § 44-69-10',
    backgroundChecks: [
      { id: 'sc_sled', label: 'SLED (State Law Enforcement Division) Criminal Check', required: true },
      { id: 'sc_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'sc_dss', label: 'DSS Central Registry of Child Abuse', required: true },
      { id: 'sc_nurse_aide', label: 'South Carolina Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'S.C. Code Ann. § 44-7-2910',
    fingerprintRequired: true,
    fingerprintType: 'SLED Fingerprint',
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: 'S.C. Code Regs. 61-77',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'S.C. Code Regs. 61-77',
    abuseRegistryName: 'SC DSS Central Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'S.C. Code Ann. § 63-7-1940',
  },

  // ==========================================
  // SOUTH DAKOTA
  // ==========================================
  SD: {
    stateCode: 'SD',
    stateName: 'South Dakota',
    requiresLicense: true,
    licensingAgency: 'South Dakota Department of Health',
    licensingAgencyAbbr: 'SD DOH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://doh.sd.gov',
    licensingPhone: '(605) 773-3361',
    licenseCitation: 'SDCL § 34-22-9',
    backgroundChecks: [
      { id: 'sd_dcr', label: 'SD Division of Criminal Investigation Check', required: true },
      { id: 'sd_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'sd_nurse_aide', label: 'South Dakota Nurse Aide Registry', required: true },
      { id: 'sd_sex_offender', label: 'South Dakota Sex Offender Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'SDCL § 34-12-59',
    fingerprintRequired: true,
    fingerprintType: 'DCI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'ARSD 44:04:15',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'ARSD 44:04:15',
    abuseRegistryName: 'SD Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'SDCL § 36-9-68',
  },

  // ==========================================
  // TENNESSEE
  // ==========================================
  TN: {
    stateCode: 'TN',
    stateName: 'Tennessee',
    requiresLicense: true,
    licensingAgency: 'Tennessee Department of Health',
    licensingAgencyAbbr: 'TDH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://www.tn.gov/health',
    licensingPhone: '(615) 741-7221',
    licenseCitation: 'T.C.A. § 68-11-201',
    backgroundChecks: [
      { id: 'tn_tbi', label: 'Tennessee Bureau of Investigation Criminal Check', required: true },
      { id: 'tn_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'tn_abuse_registry', label: 'Vulnerable Adult Abuse Registry', required: true },
      { id: 'tn_nurse_aide', label: 'Tennessee Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'T.C.A. § 68-11-256',
    fingerprintRequired: true,
    fingerprintType: 'TBI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'Tenn. Comp. R. & Regs. 1200-08-26',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'Tenn. Comp. R. & Regs. 1200-08-26',
    abuseRegistryName: 'Tennessee Abuse Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'T.C.A. § 71-6-119',
  },

  // ==========================================
  // TEXAS
  // ==========================================
  TX: {
    stateCode: 'TX',
    stateName: 'Texas',
    requiresLicense: true,
    licensingAgency: 'Texas Health and Human Services Commission',
    licensingAgencyAbbr: 'HHSC',
    licenseType: 'Home and Community Support Services Agency License',
    licensingWebsite: 'https://hhs.texas.gov',
    licensingPhone: '(512) 438-2200',
    licenseCitation: 'Tex. Health & Safety Code § 142.001',
    backgroundChecks: [
      { id: 'tx_dps', label: 'Texas DPS Criminal Background Check', required: true },
      { id: 'tx_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'tx_employee_misconduct', label: 'Employee Misconduct Registry (EMR)', required: true },
      { id: 'tx_nurse_aide', label: 'Texas Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: '26 TAC § 558.256',
    fingerprintRequired: true,
    fingerprintType: 'DPS Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '26 TAC § 558.259',
    supervisionFrequencyDays: 60,
    supervisionCitation: '26 TAC § 558.261',
    abuseRegistryName: 'Texas Employee Misconduct Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Tex. Health & Safety Code § 253.007',
    additionalRequirements: [
      'HHSC license required for home and community support services',
      'Fingerprint-based criminal history check required',
    ],
  },

  // ==========================================
  // UTAH
  // ==========================================
  UT: {
    stateCode: 'UT',
    stateName: 'Utah',
    requiresLicense: true,
    licensingAgency: 'Utah Department of Health',
    licensingAgencyAbbr: 'UDOH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://health.utah.gov',
    licensingPhone: '(801) 538-6152',
    licenseCitation: 'Utah Code § 26-21-1',
    backgroundChecks: [
      { id: 'ut_bci', label: 'Utah Bureau of Criminal Identification Check', required: true },
      { id: 'ut_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'ut_lds', label: 'Licensing Database System Check', required: true },
      { id: 'ut_nurse_aide', label: 'Utah Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Utah Code § 26-21a-101',
    fingerprintRequired: true,
    fingerprintType: 'BCI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'Utah Admin. Code R432-700',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'Utah Admin. Code R432-700',
    abuseRegistryName: 'Utah Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Utah Code § 26-21-6.5',
  },

  // ==========================================
  // VERMONT
  // ==========================================
  VT: {
    stateCode: 'VT',
    stateName: 'Vermont',
    requiresLicense: true,
    licensingAgency: 'Vermont Department of Disabilities, Aging, and Independent Living',
    licensingAgencyAbbr: 'DAIL',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://dail.vermont.gov',
    licensingPhone: '(802) 241-0294',
    licenseCitation: '33 V.S.A. § 7101',
    backgroundChecks: [
      { id: 'vt_vcic', label: 'Vermont Criminal Information Center (VCIC) Check', required: true },
      { id: 'vt_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'vt_adult_abuse', label: 'Vermont Adult Abuse Registry (33 V.S.A. § 6911)', required: true },
      { id: 'vt_child_protection', label: 'Vermont Child Protection Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: '33 V.S.A. § 7.6',
    fingerprintRequired: true,
    fingerprintType: 'VCIC Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'CVR 13-110-010',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'CVR 13-110-010',
    abuseRegistryName: 'Vermont Adult Abuse Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: '33 V.S.A. § 6911',
    additionalRequirements: [
      'Background checks required per Section 7.6 and 33 V.S.A. § 6911',
      'Adult Abuse Registry check mandatory',
    ],
  },

  // ==========================================
  // VIRGINIA
  // ==========================================
  VA: {
    stateCode: 'VA',
    stateName: 'Virginia',
    requiresLicense: true,
    licensingAgency: 'Virginia Department of Health',
    licensingAgencyAbbr: 'VDH',
    licenseType: 'Home Care Organization License',
    licensingWebsite: 'https://www.vdh.virginia.gov',
    licensingPhone: '(804) 367-2100',
    licenseCitation: '12VAC5-381',
    backgroundChecks: [
      { id: 'va_vsp', label: 'Virginia State Police Criminal Check', required: true },
      { id: 'va_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'va_sex_offender', label: 'Virginia Sex Offender Registry', required: true },
      { id: 'va_nurse_aide', label: 'Virginia Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Va. Code § 32.1-162.9:1',
    fingerprintRequired: true,
    fingerprintType: 'VSP Fingerprint',
    initialTrainingHours: 40,
    annualTrainingHours: 12,
    trainingCitation: '12VAC5-381-100',
    supervisionFrequencyDays: 60,
    supervisionCitation: '12VAC5-381-110',
    abuseRegistryName: 'Virginia Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Va. Code § 32.1-162.8',
  },

  // ==========================================
  // WASHINGTON
  // ==========================================
  WA: {
    stateCode: 'WA',
    stateName: 'Washington',
    requiresLicense: true,
    licensingAgency: 'Washington State Department of Health',
    licensingAgencyAbbr: 'DOH',
    licenseType: 'In-Home Services Agency License',
    licensingWebsite: 'https://www.doh.wa.gov',
    licensingPhone: '(360) 236-4700',
    licenseCitation: 'RCW 70.127',
    backgroundChecks: [
      { id: 'wa_wsp', label: 'Washington State Patrol Criminal Check', required: true },
      { id: 'wa_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'wa_dshs', label: 'DSHS Background Check Central Unit', required: true },
      { id: 'wa_nurse_aide', label: 'Washington Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'RCW 43.43.830',
    fingerprintRequired: true,
    fingerprintType: 'WSP Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'WAC 246-335-510',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'WAC 246-335-515',
    abuseRegistryName: 'Washington Nurse Aide Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'RCW 18.88A.020',
  },

  // ==========================================
  // WEST VIRGINIA
  // ==========================================
  WV: {
    stateCode: 'WV',
    stateName: 'West Virginia',
    requiresLicense: true,
    licensingAgency: 'West Virginia Department of Health and Human Resources',
    licensingAgencyAbbr: 'DHHR',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://dhhr.wv.gov',
    licensingPhone: '(304) 558-0050',
    licenseCitation: 'W. Va. Code § 16-5D-1',
    backgroundChecks: [
      { id: 'wv_cib', label: 'West Virginia State Police CIB Check', required: true },
      { id: 'wv_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'wv_cares', label: 'West Virginia CARES Registry', required: true },
      { id: 'wv_nurse_aide', label: 'West Virginia Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'W. Va. Code § 16-49-1',
    fingerprintRequired: true,
    fingerprintType: 'CIB Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: '64 CSR 13',
    supervisionFrequencyDays: 60,
    supervisionCitation: '64 CSR 13',
    abuseRegistryName: 'West Virginia CARES Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'W. Va. Code § 9-6-9',
  },

  // ==========================================
  // WISCONSIN
  // ==========================================
  WI: {
    stateCode: 'WI',
    stateName: 'Wisconsin',
    requiresLicense: true,
    licensingAgency: 'Wisconsin Department of Health Services',
    licensingAgencyAbbr: 'DHS',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://www.dhs.wisconsin.gov',
    licensingPhone: '(608) 266-8481',
    licenseCitation: 'Wis. Stat. § 50.49',
    backgroundChecks: [
      { id: 'wi_doj', label: 'Wisconsin DOJ Crime Information Bureau Check', required: true },
      { id: 'wi_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'wi_caregiver', label: 'Caregiver Background Check', required: true },
      { id: 'wi_nurse_aide', label: 'Wisconsin Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Wis. Stat. § 50.065',
    fingerprintRequired: true,
    fingerprintType: 'DOJ Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'Wis. Admin. Code DHS 133',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'Wis. Admin. Code DHS 133',
    abuseRegistryName: 'Wisconsin Caregiver Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Wis. Stat. § 146.40',
  },

  // ==========================================
  // WYOMING
  // ==========================================
  WY: {
    stateCode: 'WY',
    stateName: 'Wyoming',
    requiresLicense: true,
    licensingAgency: 'Wyoming Department of Health',
    licensingAgencyAbbr: 'WDH',
    licenseType: 'Home Health Agency License',
    licensingWebsite: 'https://health.wyo.gov',
    licensingPhone: '(307) 777-7656',
    licenseCitation: 'Wyo. Stat. § 35-2-901',
    backgroundChecks: [
      { id: 'wy_dci', label: 'Wyoming Division of Criminal Investigation Check', required: true },
      { id: 'wy_fbi', label: 'FBI National Criminal Check', required: true },
      { id: 'wy_abuse_registry', label: 'Central Registry of Abuse/Neglect', required: true },
      { id: 'wy_nurse_aide', label: 'Wyoming Nurse Aide Registry', required: true },
      { id: 'oig', label: 'OIG Exclusion List', required: true },
    ],
    backgroundCheckCitation: 'Wyo. Stat. § 35-2-910',
    fingerprintRequired: true,
    fingerprintType: 'DCI Fingerprint',
    initialTrainingHours: 75,
    annualTrainingHours: 12,
    trainingCitation: 'WY Rules Ch. 8, Section 4',
    supervisionFrequencyDays: 60,
    supervisionCitation: 'WY Rules Ch. 8, Section 4',
    abuseRegistryName: 'Wyoming Central Registry',
    abuseRegistryRequired: true,
    abuseRegistryCitation: 'Wyo. Stat. § 14-3-213',
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get state requirements by state code
 */
export function getStateRequirements(stateCode: string): StateRequirements | undefined {
  return STATE_REQUIREMENTS[stateCode.toUpperCase()];
}

/**
 * Get all state codes
 */
export function getAllStateCodes(): string[] {
  return Object.keys(STATE_REQUIREMENTS);
}

/**
 * Check if a state requires a license
 */
export function stateRequiresLicense(stateCode: string): boolean {
  const req = STATE_REQUIREMENTS[stateCode.toUpperCase()];
  return req ? req.requiresLicense : true;
}

/**
 * Get required background checks for a state
 */
export function getRequiredBackgroundChecks(stateCode: string): BackgroundCheckRequirement[] {
  const req = STATE_REQUIREMENTS[stateCode.toUpperCase()];
  return req ? req.backgroundChecks.filter(bc => bc.required) : [];
}

/**
 * Get the no-license states
 */
export function getNoLicenseStates(): string[] {
  return Object.entries(STATE_REQUIREMENTS)
    .filter(([_, req]) => !req.requiresLicense)
    .map(([code]) => code);
}

/**
 * Get states that require fingerprinting
 */
export function getFingerprintStates(): string[] {
  return Object.entries(STATE_REQUIREMENTS)
    .filter(([_, req]) => req.fingerprintRequired)
    .map(([code]) => code);
}
